import {
  ChangeEvent,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Option } from '~/types/types'
import { api } from '~/utils/api'

type PaymentPlanOption = {
  id: string
  name: string
}

interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  placeholder?: string
  error?: string
  onChange(value: any): void
  value?: any
  hint?: string
}

function normalizedValue(label: string) {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(' ', '-')
}

export const PlanOptionsInput: React.FC<Props> = ({
  label,
  placeholder,
  error,
  onChange,
  hint,
  value,
  ...rest
}) => {
  const [options, setOptions] = useState<Option<string | undefined>[]>([])
  const [internalValue, setInternalValue] = useState<any>()

  const inputRef = useRef<HTMLSelectElement>(null)

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newValue = options.find(
        (o) => normalizedValue(o.label) === e.target.value,
      )
      setInternalValue(newValue?.value)
      onChange(newValue?.value)
    },
    [options, onChange],
  )

  useEffect(() => {
    const abortController = new AbortController()

    api
      .get<PaymentPlanOption[]>('/admin/sales/plans/options', {
        signal: abortController.signal,
      })
      .then(({ data }) => {
        if (data.length) {
          const parsedOptions = data.reduce<Option<string | undefined>[]>(
            (accu, currVal) => {
              accu.push({
                label: currVal.name,
                value: currVal.id,
              })
              return accu
            },
            [],
          )
          setOptions([
            ...parsedOptions,
            { label: 'Qualquer plano', value: undefined },
          ])
        }
      })
      .catch(() => {
        console.log('error')
      })

    return () => {
      abortController.abort()
    }
  }, [])

  const isLoaded = useRef<boolean>(false)
  useEffect(() => {
    if (isLoaded.current) return

    if (value && options.length) {
      const defaultValue = options.find((o) => o.value === value)
      if (inputRef.current) {
        inputRef.current.value = normalizedValue(defaultValue?.label ?? '')
        setInternalValue([defaultValue])
      }
      isLoaded.current = true
    }
  }, [value, options])

  return (
    <div className='flex flex-col w-full gap-2'>
      <p>{label}</p>
      {hint ? <small>{hint}</small> : null}
      <label
        data-filled={!!internalValue}
        data-error={!!error}
        className='flex w-full bg-white rounded-md border-2
          focus-within:border-gpa-blue-500 px-2 h-10
          data-[error=true]:border-gpa-red data-[filled=true]:text-black
          data-[filled=true]:border-gpa-blue-500'
      >
        <select
          ref={inputRef}
          defaultValue=''
          data-filled={!!internalValue}
          className='flex-1 outline-none w-full
            bg-transparent focus:text-black text-slate-500 text-opacity-55
            data-[filled=true]:text-black
          '
          onChange={handleChange}
          {...rest}
        >
          <option value='' disabled hidden>
            {placeholder}
          </option>

          {options.length ? (
            options.map((option) => (
              <option
                key={`option-${option.value}`}
                value={normalizedValue(option.label)}
              >
                {option.label}
              </option>
            ))
          ) : (
            <option>Carregando...</option>
          )}
        </select>
      </label>
      {error ? <p className='text-xs text-gpa-red'>{error}</p> : null}
    </div>
  )
}
